import React, { useMemo } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  createTheme,
  Theme,
  ThemeOptions,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";

interface DatePickerInputProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  minDate?: Date;
  disabled?: boolean;
  className?: string;
}

const INPUT_HEIGHT = "32px";
const BORDER_RADIUS = "var(--radius-app)";
const BORDER_COLOR = "var(--border-color)";
const ACCENT_COLOR = "var(--color-accent)";
const HOVER_BG = "var(--color-bg-hover)";
const SELECTED_BG = "var(--bg-accent-15)";

const INPUT_BASE_STYLES = {
  borderRadius: BORDER_RADIUS,
  padding: 0,
  backgroundColor: "var(--color-bg-subtle)",
  border: `1px solid ${BORDER_COLOR}`,
  borderWidth: "1px",
  "& fieldset": {
    display: "none",
    border: "none",
  },
  "&:hover": {
    backgroundColor: HOVER_BG,
    borderColor: BORDER_COLOR,
  },
  "&.Mui-focused": {
    backgroundColor: "var(--color-bg-subtle)",
    borderColor: ACCENT_COLOR,
    "& fieldset": {
      display: "none",
      border: "none",
    },
  },
};

const BUTTON_CALENDAR_STYLES = {
  borderRadius: BORDER_RADIUS,
  borderWidth: "1px",
  border: "1px solid transparent",
  backgroundColor: "transparent",
  color: "var(--color-fg)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--font-size)",
  fontWeight: "var(--font-weight-regular)",
  minWidth: INPUT_HEIGHT,
  height: INPUT_HEIGHT,
  padding: 0,
  "&:hover": {
    backgroundColor: HOVER_BG,
  },
  "&.Mui-selected": {
    backgroundColor: SELECTED_BG,
    color: ACCENT_COLOR,
    fontWeight: "var(--font-weight-regular)",
    borderColor: ACCENT_COLOR,
    "&:hover": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
    "&:focus": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
    "&.Mui-focusVisible": {
      backgroundColor: SELECTED_BG,
      color: ACCENT_COLOR,
    },
  },
  "& .MuiTouchRipple-root": {
    display: "none",
  },
};

function buildPickerTheme(theme: Theme) {
  return createTheme({
    ...theme,
    components: {
      ...theme.components,
      MuiPaper: {
        styleOverrides: {
          root: {
            "&.MuiPickerPopper-paper": {
              backgroundColor: "var(--color-bg-subtle)",
              borderRadius: BORDER_RADIUS,
              border: `1px solid ${BORDER_COLOR}`,
              boxShadow: "none",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--font-size)",
              color: "var(--color-fg)",
              animation: "pickerSlideIn 150ms ease-out",
              "@keyframes pickerSlideIn": {
                from: {
                  opacity: 0,
                  transform: "translateY(-8px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
              "& *": {
                fontFamily: "var(--font-sans)",
                fontSize: "var(--font-size)",
                fontWeight: "var(--font-weight-regular)",
                color: "var(--color-fg)",
              },
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            minHeight: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            "&.MuiPickersTextField-root": {
              height: INPUT_HEIGHT,
              minHeight: INPUT_HEIGHT,
              maxHeight: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
            },
          },
        },
      },
      MuiPickersInputBase: {
        styleOverrides: {
          root: {
            ...INPUT_BASE_STYLES,
            height: `${INPUT_HEIGHT} !important`,
            minHeight: `${INPUT_HEIGHT} !important`,
            maxHeight: `${INPUT_HEIGHT} !important`,
            fontFamily: "var(--font-sans)",
            fontSize: "var(--font-size)",
            color: "var(--color-fg)",
            boxSizing: "border-box",
            margin: 0,
            "&.MuiPickersInputBase-adornedEnd": {
              height: `${INPUT_HEIGHT} !important`,
              minHeight: `${INPUT_HEIGHT} !important`,
              maxHeight: `${INPUT_HEIGHT} !important`,
              borderRadius: BORDER_RADIUS,
            },
          },
          input: {
            padding: "0 12px",
            fontWeight: "var(--font-weight-regular)",
          },
        },
      },
      MuiPickersSectionList: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            minHeight: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            boxSizing: "border-box",
            overflow: "hidden",
            width: "100%",
            padding: "0 12px",
            "& .MuiPickersSectionList-sectionContainer": {
              height: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: INPUT_HEIGHT,
            },
            "& .MuiPickersSectionList-section": {
              height: INPUT_HEIGHT,
              display: "flex",
              alignItems: "center",
              lineHeight: INPUT_HEIGHT,
            },
            "& .MuiPickersSectionList-sectionContent": {
              lineHeight: INPUT_HEIGHT,
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            width: "24px",
            height: "24px",
            minWidth: "24px",
            padding: 0,
            boxSizing: "border-box",
            "&:hover": {
              backgroundColor: HOVER_BG,
            },
            "&.Mui-selected": {
              backgroundColor: SELECTED_BG,
              color: ACCENT_COLOR,
            },
            "& svg": {
              width: "16px",
              height: "16px",
              fontSize: "16px",
            },
            "&.MuiIconButton-edgeEnd": {
              overflow: "visible",
            },
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: {
            borderRadius: BORDER_RADIUS,
            width: "24px",
            height: "24px",
            minWidth: "24px",
            padding: 0,
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            ...BUTTON_CALENDAR_STYLES,
            "&.MuiPickersDay-today": {
              border: `1px solid ${ACCENT_COLOR}`,
              "&.Mui-selected": {
                border: `1px solid ${ACCENT_COLOR}`,
              },
            },
          },
        },
      },
      MuiMonthCalendar: {
        styleOverrides: {
          button: {
            ...BUTTON_CALENDAR_STYLES,
          },
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          button: {
            ...BUTTON_CALENDAR_STYLES,
          },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            fontFamily: "var(--font-sans)",
            "& .MuiPickersCalendarHeader-label": {
              fontSize: "var(--font-size)",
              fontWeight: "var(--font-weight-semibold)",
              color: "var(--color-fg)",
            },
          },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            height: "fit-content",
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          slideTransition: {
            minHeight: 192,
          },
          weekDayLabel: {
            fontWeight: "var(--font-weight-medium)",
          },
        },
      },
      MuiPickersToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--color-bg-subtle)",
            borderBottom: `1px solid ${BORDER_COLOR}`,
            padding: "8px 12px",
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            height: INPUT_HEIGHT,
            maxHeight: INPUT_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 0,
            "&.MuiInputAdornment-positionEnd": {
              paddingRight: "4px",
            },
          },
        },
      },
    } as ThemeOptions["components"],
  });
}

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  minDate,
  disabled,
  className = "",
}) => {
  const theme = useTheme();
  const datePickerTheme = useMemo(() => buildPickerTheme(theme), [theme]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={datePickerTheme}>
        <DesktopDatePicker
          value={value}
          onChange={onChange}
          minDate={minDate}
          disabled={disabled}
          className={className}
          slotProps={{
            textField: {
              required: true,
              size: "small",
              fullWidth: true,
            },
          }}
          sx={{
            width: "100%",
          }}
        />
      </ThemeProvider>
    </LocalizationProvider>
  );
};
